/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MessagesStorageFileRequirements } from '../models/MessagesStorageFileRequirements';
import type { MessagesStorageStorageDetailItem } from '../models/MessagesStorageStorageDetailItem';
import type { MessagesStorageStorageDetailItemWorkspace } from '../models/MessagesStorageStorageDetailItemWorkspace';
import type { MessagesStorageStorageFileType } from '../models/MessagesStorageStorageFileType';
import type { MessagesStorageStorageList } from '../models/MessagesStorageStorageList';
import type { MessagesStorageStorageMigrationJob } from '../models/MessagesStorageStorageMigrationJob';
import type { MessagesStorageStorageSummaryItem } from '../models/MessagesStorageStorageSummaryItem';
import type { MessagesStorageStorageTransfer } from '../models/MessagesStorageStorageTransfer';
import type { SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues } from '../models/SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues';
import type { SystemNetHttpHttpResponseMessage } from '../models/SystemNetHttpHttpResponseMessage';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class StorageApiService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @param applicationId
     * @param templateId
     * @returns MessagesStorageFileRequirements OK
     * @throws ApiError
     */
    public postStorageApiStorageFileRequirements(
        applicationId?: number,
        templateId?: number,
    ): CancelablePromise<MessagesStorageFileRequirements> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/storage/fileRequirements',
            query: {
                'applicationId': applicationId,
                'templateId': templateId,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectMember)
     * @param formData
     * @returns MessagesStorageStorageTransfer OK
     * @throws ApiError
     */
    public postStorageApiUploadFile(
        formData?: {
            formData?: Array<SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues>;
        },
    ): CancelablePromise<MessagesStorageStorageTransfer> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/storage/upload_file',
            formData: formData,
            mediaType: 'multipart/form-data',
        });
    }

    /**
     *  (Authorised Roles: ProjectMember)
     * @param blobName
     * @param applicationId
     * @param projectId
     * @param fileType
     * @returns SystemNetHttpHttpResponseMessage OK
     * @throws ApiError
     */
    public getStorageApiDownloadFile(
        blobName: string,
        applicationId: number,
        projectId: number,
        fileType: MessagesStorageStorageFileType,
    ): CancelablePromise<SystemNetHttpHttpResponseMessage> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/download_file/{blobName},{applicationId},{projectId},{fileType}',
            path: {
                'blobName': blobName,
                'applicationId': applicationId,
                'projectId': projectId,
                'fileType': fileType,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, SuperUser)
     * @param blobName
     * @param subscriptionId
     * @param fileType
     * @returns SystemNetHttpHttpResponseMessage OK
     * @throws ApiError
     */
    public getStorageApiDownloadFileWorkspace(
        blobName?: string,
        subscriptionId?: number,
        fileType?: MessagesStorageStorageFileType,
    ): CancelablePromise<SystemNetHttpHttpResponseMessage> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/download_file/workspace',
            query: {
                'blobName': blobName,
                'subscriptionId': subscriptionId,
                'fileType': fileType,
            },
        });
    }

    /**
     * Get a summary of storage/files, including counts per type (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, SuperUser)
     * @param applicationId
     * @param projectId
     * @returns MessagesStorageStorageSummaryItem OK
     * @throws ApiError
     */
    public getStorageApiGetFilesSummary(
        applicationId: number,
        projectId: number,
    ): CancelablePromise<Array<MessagesStorageStorageSummaryItem>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/summary/{applicationId},{projectId}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
            },
        });
    }

    /**
     * Get a detailed list of files for a type, including filename, size, current indicator (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, SuperUser)
     * @param applicationId
     * @param projectId
     * @param type
     * @returns MessagesStorageStorageDetailItem OK
     * @throws ApiError
     */
    public getStorageApiGetFilesDetails(
        applicationId: number,
        projectId: number,
        type: MessagesStorageStorageFileType,
    ): CancelablePromise<Array<MessagesStorageStorageDetailItem>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/details/{applicationId},{projectId},{type}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
                'type': type,
            },
        });
    }

    /**
     * Get a detailed list of files for a workspace, including filename, size, current indicator (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, SuperUser)
     * @param workspaceId
     * @returns MessagesStorageStorageDetailItemWorkspace OK
     * @throws ApiError
     */
    public getStorageApiGetFilesDetailsWorkspace(
        workspaceId?: number,
    ): CancelablePromise<Array<MessagesStorageStorageDetailItemWorkspace>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/details/workspace',
            query: {
                'workspaceId': workspaceId,
            },
        });
    }

    /**
     * Get a detailed list of files for an entity, including filename, size, current indicator (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, SuperUser)
     * @param entityId
     * @returns MessagesStorageStorageDetailItem OK
     * @throws ApiError
     */
    public getStorageApiGetFilesDetailsEntityTemplates(
        entityId?: number,
    ): CancelablePromise<Array<MessagesStorageStorageDetailItem>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/details/entityTemplates',
            query: {
                'entityId': entityId,
            },
        });
    }

    /**
     * Get a detailed list of files for an entity, including filename, size (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, SuperUser)
     * @param entityId
     * @returns MessagesStorageStorageDetailItem OK
     * @throws ApiError
     */
    public getStorageApiGetFilesDetailsEntityDataAssets(
        entityId?: number,
    ): CancelablePromise<Array<MessagesStorageStorageDetailItem>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/details/entityDataAssets',
            query: {
                'entityId': entityId,
            },
        });
    }

    /**
     * Deletes all files of a particular type (for project/application) (Authorised Roles: ProjectAdministrator, ProjectLeader, SuperUser)
     * @param applicationId
     * @param projectId
     * @param type
     * @returns MessagesStorageStorageList OK
     * @throws ApiError
     */
    public getStorageApiDeleteAll(
        applicationId: number,
        projectId: number,
        type: MessagesStorageStorageFileType,
    ): CancelablePromise<Array<MessagesStorageStorageList>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/delete-all/{applicationId},{projectId},{type}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
                'type': type,
            },
        });
    }

    /**
     * Deletes all "unused" files of a particular type (for project/application)
     * eg. old template uploads (Authorised Roles: ProjectAdministrator, ProjectLeader, SuperUser)
     * @param applicationId
     * @param projectId
     * @param type
     * @returns MessagesStorageStorageList OK
     * @throws ApiError
     */
    public getStorageApiDeleteUnused(
        applicationId: number,
        projectId: number,
        type: MessagesStorageStorageFileType,
    ): CancelablePromise<Array<MessagesStorageStorageList>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/delete-unused/{applicationId},{projectId},{type}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
                'type': type,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, SuperUser)
     * @param blobName
     * @param subscriptionId
     * @param fileType
     * @returns boolean OK
     * @throws ApiError
     */
    public postStorageApiDeleteFileWorkspace(
        blobName?: string,
        subscriptionId?: number,
        fileType?: MessagesStorageStorageFileType,
    ): CancelablePromise<boolean> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/storage/delete_file/workspace',
            query: {
                'blobName': blobName,
                'subscriptionId': subscriptionId,
                'fileType': fileType,
            },
        });
    }

    /**
     * Migrates files for a project/application from the old storage format to the new storage format
     * Old: All files as blobs in a single container
     * New: Files structured in "folders" (Authorised Roles: ProjectAdministrator, ProjectLeader, SuperUser)
     * @param applicationId
     * @param projectId
     * @param apply With this set to false, no action will be performed, only old files enumerated to see if everything can be identified/migrated.
     * @returns MessagesStorageStorageMigrationJob OK
     * @throws ApiError
     */
    public getStorageApiMigrate(
        applicationId: number,
        projectId: number,
        apply: boolean,
    ): CancelablePromise<MessagesStorageStorageMigrationJob> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/migrate/{applicationId},{projectId},{apply}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
                'apply': apply,
            },
        });
    }

    /**
     * Gets details of job busy being applied (Authorised Roles: ProjectAdministrator, ProjectLeader, SuperUser)
     * @param applicationId
     * @param projectId
     * @param jobId
     * @returns MessagesStorageStorageMigrationJob OK
     * @throws ApiError
     */
    public getStorageApiGetMigrationJob(
        applicationId: number,
        projectId: number,
        jobId: string,
    ): CancelablePromise<MessagesStorageStorageMigrationJob> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/storage/migrate/{applicationId},{projectId},{jobId}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
                'jobId': jobId,
            },
        });
    }

}
